/**
 * Renderer Dispatcher · maps action.type → React component.
 * Add new renderers here as we build them out per status.
 *
 * Each renderer receives:
 *   action        — the seeded action object (type, params, ...)
 *   primitive     — the primitive registry entry (label, description, param_schema)
 *   capturedData  — the full run's captured_data map (read-only reference)
 *   value         — current in-flight value for this action
 *   onChange(v)   — update the in-flight value
 *   onNext()      — commit + advance to next action
 *   onBack()      — jump backward
 *   canBack       — whether Back should show
 */
import React from 'react';
import ScriptedFieldPrompt from './renderers/ScriptedFieldPrompt';
import RadioChoice from './renderers/RadioChoice';
import GotoForm from './renderers/GotoForm';
import CheckboxTick from './renderers/CheckboxTick';
import DatabaseSearchSelect from './renderers/DatabaseSearchSelect';
import OpenIntakeForm from './renderers/OpenIntakeForm';
import OpenPropertyIntakeForm from './renderers/OpenPropertyIntakeForm';
import CreateNewPropertyForm from './renderers/CreateNewPropertyForm';
import AutoCreatePropertyFromClient from './renderers/AutoCreatePropertyFromClient';
import PrimaryQuoteTypePicker from './renderers/PrimaryQuoteTypePicker';
import PreferredTimeCapture from './renderers/PreferredTimeCapture';
import AppraiserPicker from './renderers/AppraiserPicker';
import DisplayMessage from './renderers/DisplayMessage';
import RoofScreenshotCapture from './renderers/RoofScreenshotCapture';
import RoofTemplatePick from './renderers/RoofTemplatePick';
import ClientDetailsConfirm from './renderers/ClientDetailsConfirm';
import PropertyDetailsConfirm from './renderers/PropertyDetailsConfirm';
import RoofVerify from './renderers/RoofVerify';
import TenantAgentConfirm from './renderers/TenantAgentConfirm';
import LetterPreview from './renderers/LetterPreview';
// iter303ak-fix22c · Parent-Child aware letter preview for Task 3 Action 2
import LetterPreviewPerChild from './renderers/LetterPreviewPerChild';
import SmsPreview from './renderers/SmsPreview';
import ChannelPicker from './renderers/ChannelPicker';
import SendBundle from './renderers/SendBundle';
import AwaitExternalConfirm from './renderers/AwaitExternalConfirm';
// iter303ak — Quote Sent renderers (Tasks 1, 2, 5)
import DesktopRecon from './renderers/DesktopRecon';
// iter303ak-fix2 — Quote Sent Task 1 refined renderers
import RoofSnapshotConfirm from './renderers/RoofSnapshotConfirm';
import AnnotateRoofDiagram from './renderers/AnnotateRoofDiagram';
import CompletenessPreview from './renderers/CompletenessPreview';
import QuoteBuilderLauncher from './renderers/QuoteBuilderLauncher';
import JobShellsSeed from './renderers/JobShellsSeed';
import FollowupReminder from './renderers/FollowupReminder';
// iter303ak-fix23 · Await client accept/decline decision (Task 50 Action 20)
import AwaitQuoteResponse from './renderers/AwaitQuoteResponse';
// iter303ak-fix23 · Accepted status — Task 10 primitives
import PickAcceptedVariants from './renderers/PickAcceptedVariants';
import DepositCapture from './renderers/DepositCapture';
// iter303ak-fix23f · Approximate start-time estimator (T10 A15)
import ApproxStartEstimator from './renderers/ApproxStartEstimator';
// iter303ak-fix23k · Per-child variants of the start-time + deposit primitives
import PerChildApproxStart from './renderers/PerChildApproxStart';
import PerChildDepositCapture from './renderers/PerChildDepositCapture';
// iter303ak-fix24 · Accepted T30 Contractor Report Raised renderers
import PerChildComponentsReview from './renderers/PerChildComponentsReview';
import PerChildContractorsReview from './renderers/PerChildContractorsReview';
import PerChildContractorReportPreview from './renderers/PerChildContractorReportPreview';
import PerChildContractorSendBundle from './renderers/PerChildContractorSendBundle';
// iter303ak-fix25 · Declined-pipeline renderers
import DeclinedSummary from './renderers/DeclinedSummary';
import ClientReplyCapture from './renderers/ClientReplyCapture';
import SpawnAppraisalVariant from './renderers/SpawnAppraisalVariant';
// iter303ak-fix26 · Height Safety two-stage flow renderers
import HeightSafetyTypePicker from './renderers/HeightSafetyTypePicker';
import HeightSafetyEstimate from './renderers/HeightSafetyEstimate';
import PushToQuoteHealthSafety from './renderers/PushToQuoteHealthSafety';
import HeightSafetyQuoteRequestPreview from './renderers/HeightSafetyQuoteRequestPreview';
import HeightSafetyQuoteRequestSend from './renderers/HeightSafetyQuoteRequestSend';
import SupplierQuoteCaptureMulti from './renderers/SupplierQuoteCaptureMulti';
import SupplierPickWinner from './renderers/SupplierPickWinner';
import SupplierAlternatesPanel from './renderers/SupplierAlternatesPanel';
// iter303ak-fix27 · Materials Cost Confirm renderers (Accepted T50)
import PerChildMaterialsReview from './renderers/PerChildMaterialsReview';
import PerChildMaterialsOrderPreview from './renderers/PerChildMaterialsOrderPreview';
import PerChildMaterialsSendBundle from './renderers/PerChildMaterialsSendBundle';
// iter303ak-fix28 · Work Booked renderers (T10-T70)
import JobScheduleLauncher from './renderers/JobScheduleLauncher';
import WeatherCalendarPanel from './renderers/WeatherCalendarPanel';
import ContractorScheduleConfirmSend from './renderers/ContractorScheduleConfirmSend';
import ContractorScheduleReplyCapture from './renderers/ContractorScheduleReplyCapture';
import SafetyScheduleConfirmSend from './renderers/SafetyScheduleConfirmSend';
import SafetyScheduleReplyCapture from './renderers/SafetyScheduleReplyCapture';
import MaterialsDeliveryDatePicker from './renderers/MaterialsDeliveryDatePicker';
import ContractorDocsPreview from './renderers/ContractorDocsPreview';
import ContractorDocsSendBundle from './renderers/ContractorDocsSendBundle';
// iter303ak-fix29 · Work In Progress renderers (T10-T50)
import MaterialsReceiptConfirm from './renderers/MaterialsReceiptConfirm';
import SafetyErectionConfirm from './renderers/SafetyErectionConfirm';
import ContractorGreenlightSend from './renderers/ContractorGreenlightSend';
import WorkExecutionLauncher from './renderers/WorkExecutionLauncher';
// iter303ak-fix33 · Job Completed renderers (T10-T40)
import QualityInspectionCapture from './renderers/QualityInspectionCapture';
import ContractorPaymentFinalise from './renderers/ContractorPaymentFinalise';
import CertificateOfCompletionReview from './renderers/CertificateOfCompletionReview';
// iter303ak-fix37 · Invoiced renderers (T10-T50)
import InvoicedPaymentOverview from './renderers/InvoicedPaymentOverview';
import InvoicedBankCheck from './renderers/InvoicedBankCheck';
import InvoicedPaymentCapture from './renderers/InvoicedPaymentCapture';
import InvoicedRoutePick from './renderers/InvoicedRoutePick';
import OverduePaymentRecheck from './renderers/OverduePaymentRecheck';
import OverdueContactCallSheet from './renderers/OverdueContactCallSheet';
import OverdueWarningCallSheet from './renderers/OverdueWarningCallSheet';
import PaymentPlanBuilder from './renderers/PaymentPlanBuilder';
import GenericFallback from './renderers/GenericFallback';
// iter303ak-fix67 · F1 (P0 · reviewer) — real behaviour for the top-3
// side-effecting primitives (was silently falling to GenericFallback).
import StampDateTime from './renderers/StampDateTime';
import SendLetter from './renderers/SendLetter';
import CloseWorkflow from './renderers/CloseWorkflow';
// Jun 2026 — the nine former "Coming soon" primitives.
import FieldWriteback from './renderers/FieldWriteback';
import SpawnTask from './renderers/SpawnTask';
import CaptureNote from './renderers/CaptureNote';
import ReasonPicker from './renderers/ReasonPicker';
import OptionalInclusion from './renderers/OptionalInclusion';
import LetterContentToggle from './renderers/LetterContentToggle';
import TieredOffering from './renderers/TieredOffering';
import PdfPreviewStep from './renderers/PdfPreviewStep';

const RENDERERS = {
  scripted_field_prompt: ScriptedFieldPrompt,
  radio_choice: RadioChoice,
  goto_form: GotoForm,
  checkbox_tick: CheckboxTick,
  database_search_select: DatabaseSearchSelect,
  open_intake_form: OpenIntakeForm,
  open_property_intake_form: OpenPropertyIntakeForm,
  create_new_property_form: CreateNewPropertyForm,
  auto_create_property_from_client: AutoCreatePropertyFromClient,
  // Iter303ah — Appraisal Booked primitives
  primary_quote_type_picker: PrimaryQuoteTypePicker,
  preferred_time_capture: PreferredTimeCapture,
  appraiser_picker: AppraiserPicker,
  // iter303ai-fix8 — Schedule Appraisal sub-flow renderers
  display_message: DisplayMessage,
  roof_screenshot_capture: RoofScreenshotCapture,
  roof_template_pick: RoofTemplatePick,
  // iter303ai-fix9 — Confirm-step renderers (Actions 7 + 8a/8b/8c)
  client_details_confirm: ClientDetailsConfirm,
  property_details_confirm: PropertyDetailsConfirm,
  roof_verify: RoofVerify,
  tenant_agent_confirm: TenantAgentConfirm,
  // iter303aj — Notify Client renderers (Task 2)
  letter_preview: LetterPreview,
  // iter303ak-fix22c · Parent-Child aware companion — one PDF per child
  letter_preview_per_child: LetterPreviewPerChild,
  sms_preview: SmsPreview,
  channel_picker: ChannelPicker,
  send_bundle: SendBundle,
  // iter303aj-fix11 — Confirm Appraiser (Task 3)
  await_external_confirm: AwaitExternalConfirm,
  // iter303ak — Quote Sent renderers (Tasks 1, 2, 5)
  desktop_recon: DesktopRecon,
  // iter303ak-fix2 — Quote Sent Task 1 refined renderers
  roof_snapshot_confirm: RoofSnapshotConfirm,
  annotate_roof_diagram: AnnotateRoofDiagram,
  completeness_preview: CompletenessPreview,
  quote_builder_launcher: QuoteBuilderLauncher,
  job_shells_seed: JobShellsSeed,
  followup_reminder: FollowupReminder,
  // iter303ak-fix23 · Follow-up · Action 20 — wait for reply, route to Accepted/Declined
  await_quote_response: AwaitQuoteResponse,
  // iter303ak-fix23 · Accepted status · Task 10 primitives
  pick_accepted_variants: PickAcceptedVariants,
  deposit_capture: DepositCapture,
  // iter303ak-fix23f · Accepted T10 A15 approximate start-time picker
  approx_start_estimator: ApproxStartEstimator,
  // iter303ak-fix23k · Per-child variants (Trevor's A5184 walkthrough:
  // multiple accepted quotes need different start times + deposits)
  per_child_approx_start: PerChildApproxStart,
  per_child_deposit_capture: PerChildDepositCapture,
  // iter303ak-fix24 · Accepted T30 · Contractor Report Raised primitives.
  // Per-child accordions driving Components review → Contractors review →
  // per-contractor PDF preview → per-contractor MOCKED send bundle.
  per_child_components_review: PerChildComponentsReview,
  per_child_contractors_review: PerChildContractorsReview,
  per_child_contractor_report_preview: PerChildContractorReportPreview,
  per_child_contractor_send_bundle: PerChildContractorSendBundle,
  // iter303ak-fix25 · Declined-pipeline primitives.
  declined_summary: DeclinedSummary,
  client_reply_capture: ClientReplyCapture,
  spawn_appraisal_variant: SpawnAppraisalVariant,
  // iter303ak-fix26 · Height Safety two-stage flow primitives.
  height_safety_type_picker: HeightSafetyTypePicker,
  height_safety_estimate: HeightSafetyEstimate,
  push_to_quote_health_safety: PushToQuoteHealthSafety,
  height_safety_quote_request_preview: HeightSafetyQuoteRequestPreview,
  height_safety_quote_request_send: HeightSafetyQuoteRequestSend,
  supplier_quote_capture_multi: SupplierQuoteCaptureMulti,
  supplier_pick_winner: SupplierPickWinner,
  supplier_alternates_panel: SupplierAlternatesPanel,
  // iter303ak-fix27 · Materials Cost Confirm primitives (Accepted T50).
  per_child_materials_review: PerChildMaterialsReview,
  per_child_materials_order_preview: PerChildMaterialsOrderPreview,
  per_child_materials_send_bundle: PerChildMaterialsSendBundle,
  // iter303ak-fix28 · Work Booked primitives (T10-T70).
  job_schedule_launcher: JobScheduleLauncher,
  weather_calendar_panel: WeatherCalendarPanel,
  contractor_schedule_confirm_send: ContractorScheduleConfirmSend,
  contractor_schedule_reply_capture: ContractorScheduleReplyCapture,
  safety_schedule_confirm_send: SafetyScheduleConfirmSend,
  safety_schedule_reply_capture: SafetyScheduleReplyCapture,
  materials_delivery_date_picker: MaterialsDeliveryDatePicker,
  contractor_docs_preview: ContractorDocsPreview,
  contractor_docs_send_bundle: ContractorDocsSendBundle,
  // iter303ak-fix29 · Work In Progress primitives (T10-T50).
  materials_receipt_confirm: MaterialsReceiptConfirm,
  safety_erection_confirm: SafetyErectionConfirm,
  contractor_greenlight_send: ContractorGreenlightSend,
  work_execution_launcher: WorkExecutionLauncher,
  // iter303ak-fix33 · Job Completed primitives (T10-T40).
  quality_inspection_capture: QualityInspectionCapture,
  contractor_payment_finalise: ContractorPaymentFinalise,
  certificate_of_completion_review: CertificateOfCompletionReview,
  // iter303ak-fix37 · Invoiced renderers (T10-T50)
  invoiced_payment_overview: InvoicedPaymentOverview,
  invoiced_bank_check: InvoicedBankCheck,
  invoiced_payment_capture: InvoicedPaymentCapture,
  invoiced_route_pick: InvoicedRoutePick,
  // iter303ak-fix39 · Overdue T10 recheck primitive.
  overdue_payment_recheck: OverduePaymentRecheck,
  overdue_contact_callsheet: OverdueContactCallSheet,
  overdue_warning_callsheet: OverdueWarningCallSheet,
  payment_plan_builder: PaymentPlanBuilder,
  // iter303ak-fix67 · F1 (P0 · reviewer) — side-effecting primitives
  // now have real behaviour instead of silent GenericFallback ack.
  stamp_datetime: StampDateTime,
  send_letter:    SendLetter,
  close_workflow: CloseWorkflow,
  field_writeback: FieldWriteback,
  spawn_task: SpawnTask,
  capture_note: CaptureNote,
  reason_picker: ReasonPicker,
  optional_inclusion: OptionalInclusion,
  letter_content_toggle: LetterContentToggle,
  tiered_offering: TieredOffering,
  generate_pdf_preview: PdfPreviewStep,
  preview_confirm_gate: PdfPreviewStep,
  // Auto-executing primitives (Phase 0: acknowledge-only)
  conditional_branch: GenericFallback,
  create_client_folder: GenericFallback,
  confirm_fields: GenericFallback,
};

export default function dispatchRenderer(actionType) {
  return RENDERERS[actionType] || GenericFallback;
}
